import React, { useEffect, useId, useRef, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import axios from 'axios';
import Modal from './Modal';
import { Button, Input } from '../shared/ui';
import { getSettlementDates } from '../services/settlementDateService';
import { API_URL } from '../config/env';
import styles from './VoucherLookupPopup.module.css';

const MAX_ROWS = 10;

function formatAmount(voucher) {
  const n =
    typeof voucher.total === 'number'
      ? voucher.total
      : Number(voucher.amount ?? voucher.total ?? 0);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

export default function VoucherLookupPopup({ open, onClose }) {
  const formId = useId();
  const searchInputId = `${formId}-q`;
  const searchRef = useRef(null);

  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [dateRanges, setDateRanges] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  /** True after user has run at least one search while this session is open — avoids premature “No results”. */
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setResults([]);
      setDateRanges({});
      setError('');
      setHasSearched(false);
      setLoading(false);
      return;
    }
    const focus = () => searchRef.current?.focus?.({ preventScroll: true });
    const id = requestAnimationFrame(() => focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/vouchers`, {
        params: { search },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const vouchers = res.data.slice(0, MAX_ROWS);
      setResults(vouchers);

      const settlementIds = [...new Set(vouchers.map((v) => v.settlement_date_id).filter(Boolean))];
      if (settlementIds.length > 0) {
        const allDates = await getSettlementDates();
        const map = {};
        for (const d of allDates) {
          map[d.id] = d.date_range;
        }
        setDateRanges(map);
      } else {
        setDateRanges({});
      }
      setHasSearched(true);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch vouchers.');
      setResults([]);
      setDateRanges({});
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Voucher lookup"
      description="Search agreements, vouchers, students, or case numbers across the workspace."
      size="lg"
    >
      <div className={styles.wrap}>
        <section className={styles.searchCard}>
          <form id={formId} onSubmit={handleSearch} className={styles.searchForm}>
            <Input
              ref={searchRef}
              id={searchInputId}
              label="Search"
              placeholder="Agreement #, voucher ID, student name, case #…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
              className={styles.searchField}
            />
            <div className={styles.searchSubmit}>
              <Button type="submit" variant="primary" iconLeft={<FaSearch />} loading={loading}>
                Search
              </Button>
            </div>
          </form>
          <p className={styles.searchHint}>Press Enter to search. Showing up to {MAX_ROWS} matches.</p>
        </section>

        <div className={styles.stateBlock}>
          {loading ? (
            <div className={styles.loadingWrap} role="status" aria-live="polite">
              <span className={styles.loadingPulse} aria-hidden />
              Searching vouchers…
            </div>
          ) : error ? (
            <div className={`${styles.alert} ${styles.alertError}`} role="alert">
              {error}
            </div>
          ) : hasSearched && results.length === 0 ? (
            <div className={styles.empty}>
              <p className={styles.resultsTitle}>No matches</p>
              <p className={styles.emptyMuted}>
                Try another keyword, voucher ID fragment, or child name spelling.
              </p>
            </div>
          ) : !hasSearched ? (
            <div className={styles.empty}>
              <p className={styles.resultsTitle}>Find a voucher</p>
              <p className={styles.emptyMuted}>
                Enter any identifier you have — we'll search vouchers and linked records.
              </p>
            </div>
          ) : (
            <>
              <div className={styles.resultsHead}>
                <h4 className={styles.resultsTitle}>Results</h4>
                <span className={styles.resultsMeta}>{results.length} shown</span>
              </div>
              <div className={styles.tableScroller}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th scope="col">Voucher ID</th>
                      <th scope="col">Student</th>
                      <th scope="col">Case #</th>
                      <th scope="col">Agreement #</th>
                      <th scope="col">Amount</th>
                      <th scope="col">Status</th>
                      <th scope="col">Settlement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((voucher) => (
                      <tr key={voucher.id}>
                        <td className={styles.mono}>{voucher.id}</td>
                        <td>{voucher.student_name || voucher.student_id || voucher.child_name || '—'}</td>
                        <td className={styles.mono}>{voucher.case_number ?? '—'}</td>
                        <td>{voucher.agreement_number ?? '—'}</td>
                        <td className={styles.amount}>{formatAmount(voucher)}</td>
                        <td>
                          {voucher.status ? (
                            <span className={styles.statusPill}>{voucher.status}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>{voucher.settlement_date_id ? dateRanges[voucher.settlement_date_id] || '—' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
